import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, useTheme, Button, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './MarketingStrategy.css';

const CorporateCulture: React.FC = () => {
  const theme = useTheme();
  const [totalPages, setTotalPages] = useState(0);
  const [pages, setPages] = useState<(string | null)[]>([]);
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [activeSlide, setActiveSlide] = useState(0);
  const pdfDocRef = useRef<any>(null);

  // Initialize PDF.js worker
  useEffect(() => {
    GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
    const loadPdf = async () => {
      try {
        const response = await fetch('/NEXTBASKET_Corporate_Culture.pdf');
        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);
        
        const loadingTask = getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        pdfDocRef.current = pdf;
        setTotalPages(pdf.numPages);
        
        // Initialize array with null placeholders
        const emptyPages = Array(pdf.numPages).fill(null);
        setPages(emptyPages);
        
        // Render only the first page initially
        await renderPage(0);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err instanceof Error ? err.message : 'Failed to load PDF');
        setLoading(false);
      }
    };
    
    loadPdf();
    
    // Cleanup function
    return () => {
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, []); // Only run on mount
  
  // Function to render a specific page
  const renderPage = async (pageIndex: number) => {
    if (!pdfDocRef.current || renderedPages.has(pageIndex) || pageIndex < 0 || pageIndex >= totalPages) {
      return;
    }
    
    try {
      const page = await pdfDocRef.current.getPage(pageIndex + 1); // PDF pages are 1-indexed
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context!,
        viewport: viewport
      }).promise;
      
      const dataUrl = canvas.toDataURL();
      
      setPages(prevPages => {
        const newPages = [...prevPages];
        newPages[pageIndex] = dataUrl;
        return newPages;
      });
      
      setRenderedPages(prev => new Set(Array.from(prev).concat([pageIndex])));
      
      // Clean up
      canvas.width = 0;
      canvas.height = 0;
    } catch (err) {
      console.error(`Error rendering page ${pageIndex + 1}:`, err);
    }
  };
  
  // Effect to render visible and adjacent pages when active slide changes
  useEffect(() => {
    if (loading || !pdfDocRef.current) return;
    
    // Render current page and adjacent pages
    const pagesToRender = [
      activeSlide - 1, // previous
      activeSlide,    // current
      activeSlide + 1  // next
    ].filter(index => index >= 0 && index < totalPages);
    
    // For debugging
    // console.log('Rendering pages:', pagesToRender);
    
    // Render pages
    Promise.all(pagesToRender.map(index => renderPage(index)));
    
    // Optional: Unload far pages to save memory
    if (renderedPages.size > 5) { // Keep max 5 pages in memory
      const pagesToKeep = new Set(pagesToRender);
      const newRenderedPages = new Set<number>();
      
      // Convert Set to Array for iteration to avoid TypeScript issues
      Array.from(renderedPages).forEach(pageIndex => {
        if (pagesToKeep.has(pageIndex)) {
          newRenderedPages.add(pageIndex);
        } else {
          // Unload page that's far from current view
          setPages(prevPages => {
            const newPages = [...prevPages];
            if (Math.abs(pageIndex - activeSlide) > 2) { // Unload pages more than 2 steps away
              newPages[pageIndex] = null;
            }
            return newPages;
          });
        }
      });
      
      setRenderedPages(newRenderedPages);
    }
  }, [activeSlide, loading, totalPages, renderedPages]);
  
  // Effect to re-render current page when scale changes
  useEffect(() => {
    if (!loading && activeSlide >= 0 && activeSlide < totalPages) {
      // Remove the page from rendered set to force re-render
      setRenderedPages(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(activeSlide);
        return newSet;
      });
      
      // Re-render the current page with new scale
      renderPage(activeSlide);
    }
  }, [scale]);

  return (
    <Box sx={{ width: '100%', height: '100%', mb: 6, overflow: 'hidden' }}>
      <Typography
        variant="h3"
        component="h2"
        align="center"
        sx={{
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
          fontWeight: 700,
          mb: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 70%, #1a4b80 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textTransform: 'uppercase',
          '&::after': {
            content: '""',
            display: 'block',
            width: '60px',
            height: '4px',
            backgroundColor: theme.palette.primary.main,
            margin: '16px auto 0',
            borderRadius: '2px'
          }
        }}
      >
        COMPANY CULTURE CODE
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: { xs: '0.85rem', sm: '0.9rem' },
          opacity: 0.8,
          mb: { xs: 1, sm: 2 }
        }}
      >
        32 slides available
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: { xs: '0.85rem', sm: '0.9rem' },
          opacity: 0.8,
          mb: { xs: 3, sm: 4 }
        }}
      >
        Swipe or use arrows for more content
      </Typography>

      <Box
        sx={{
          mt: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          border: '1px solid rgba(63, 160, 255, 0.3)',
          p: 0,
          position: 'relative',
          height: { xs: '600px', md: 'calc(100vh - 50px)' },
          minHeight: '600px',
          overflow: 'hidden',
          '& .zoom-controls': {
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            display: 'flex',
            gap: 1,
            padding: '4px'
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(63, 160, 255, 0.1) 0%, rgba(63, 160, 255, 0.05) 100%)',
            borderRadius: 'inherit',
            pointerEvents: 'none'
          }
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 10,
              borderRadius: 'inherit',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid rgba(63, 160, 255, 0.3)',
                  borderTop: '3px solid rgba(63, 160, 255, 1)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              />
              <Typography sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                Loading PDF...
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Typography
            color="error"
            sx={{
              textAlign: 'center',
              mt: 4
            }}
          >
            {error}
          </Typography>
        )}



        {!loading && !error && (
          <>
            <Box className="zoom-controls">
              <IconButton
                onClick={() => setScale(prev => Math.max(prev - 0.25, 0.5))}
                disabled={scale <= 0.5}
                sx={{
                  width: 44,
                  height: 44,
                  color: '#50b5ff',
                  border: '1px solid rgba(80, 181, 255, 0.3)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.5rem',
                    transition: 'all 0.3s ease'
                  },
                  '&:hover': { 
                    transform: 'translateY(-2px)',
                    color: '#80cfff',
                    backgroundColor: 'transparent',
                    '& .MuiSvgIcon-root': {
                      transform: 'scale(1.1)',
                      filter: 'drop-shadow(0 0 5px rgba(63, 160, 255, 0.5))'
                    }
                  },
                  '&:active': {
                    transform: 'translateY(1px)'
                  },
                  '&.Mui-disabled': { 
                    color: 'rgba(63, 160, 255, 0.3)',
                    borderColor: 'rgba(63, 160, 255, 0.1)'
                  }
                }}
              >
                <ZoomOutIcon />
              </IconButton>
              <IconButton
                onClick={() => setScale(prev => Math.min(prev + 0.25, 3))}
                disabled={scale >= 3}
                sx={{
                  width: 44,
                  height: 44,
                  color: '#50b5ff',
                  border: '1px solid rgba(80, 181, 255, 0.3)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.5rem',
                    transition: 'all 0.3s ease'
                  },
                  '&:hover': { 
                    transform: 'translateY(-2px)',
                    color: '#80cfff',
                    backgroundColor: 'transparent',
                    '& .MuiSvgIcon-root': {
                      transform: 'scale(1.1)',
                      filter: 'drop-shadow(0 0 5px rgba(63, 160, 255, 0.5))'
                    }
                  },
                  '&:active': {
                    transform: 'translateY(1px)'
                  },
                  '&.Mui-disabled': { 
                    color: 'rgba(63, 160, 255, 0.3)',
                    borderColor: 'rgba(63, 160, 255, 0.1)'
                  }
                }}
              >
                <ZoomInIcon />
              </IconButton>
            </Box>

            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={0}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              onSlideChange={(swiper: SwiperType) => setActiveSlide(swiper.activeIndex)}
              style={{ width: '100%', height: '100%' }}
              className="pdf-swiper"
            >
              {Array.from({ length: totalPages }).map((_, index) => (
                <SwiperSlide key={index} style={{ width: '100%', height: '100%' }}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      p: 0
                    }}
                  >
                    {pages[index] ? (
                      <img
                        src={pages[index] || ''} 
                        alt={`Page ${index + 1}`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          transform: activeSlide === index ? `scale(${scale})` : 'scale(1)',
                          transformOrigin: 'center center',
                          transition: 'transform 0.2s ease-out',
                          willChange: 'transform'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0, 0, 0, 0.03)'
                        }}
                      >
                        <Box
                          sx={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid rgba(63, 160, 255, 0.3)',
                            borderTop: '3px solid rgba(63, 160, 255, 1)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': {
                                transform: 'rotate(0deg)',
                              },
                              '100%': {
                                transform: 'rotate(360deg)',
                              },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <br /><br /><br />
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.primary',
            mb: 2
          }}
        >
          Download our detailed Corporate Culture presentation to learn more about NEXT BASKET<br />
          <br />
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => {
            try {
              const link = document.createElement('a');
              link.href = '/NEXTBASKET_Corporate_Culture.pdf';
              link.download = 'NEXTBASKET_Corporate_Culture.pdf';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } catch (error) {
              console.error('Error downloading file:', error);
            }
          }}
          startIcon={<DownloadIcon />}
          sx={{
            py: { xs: 1.75, md: 1.75 },
            px: { xs: 4, md: 5 },
            minWidth: { xs: 'auto', md: '300px' },
            borderRadius: 15,
            textTransform: 'none',
            fontSize: { xs: '1.2rem', md: '1.1rem' },
            fontWeight: 600,
            letterSpacing: '0.02em',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: '#fff',
            border: '1px solid rgba(63, 160, 255, 0.3)',
            backdropFilter: 'blur(8px)',
            zIndex: theme.zIndex.modal + 1,
            '& .MuiSvgIcon-root': {
              color: '#fff',
              fontSize: '1.4rem',
              marginRight: '8px',
              transition: 'transform 0.3s ease'
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
              transform: 'translateX(-100%) skewX(-15deg)',
              transition: 'transform 0.7s ease'
            },
            '&:hover': {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: '0 8px 25px rgba(63, 160, 255, 0.4)',
              transform: 'translateY(-2px)',
              border: '1px solid rgba(63, 160, 255, 0.5)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.1) rotate(-10deg)'
              },
              '&::before': {
                transform: 'translateX(100%) skewX(-15deg)'
              }
            },
            '&:active': {
              transform: 'translateY(1px)',
              boxShadow: '0 4px 15px rgba(63, 160, 255, 0.3)'
            }
          }}
        >
          Download PDF
          <br />
        </Button>
        <br />
      </Box>
    </Box>
  );
};

export default CorporateCulture;
